<template>
  <div class="w-full max-w-full mx-auto grid gap-2">
    <Label>{{ label }}</Label>
    
    <div
      class="relative min-h-[200px] max-h-[200px] rounded-lg border-2 border-dashed transition-all duration-300 flex items-center justify-center cursor-pointer"
      :class="[ 
        isDragging && !preview
          ? 'border-emerald-400 bg-emerald-400/5' 
          : 'border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10',
        (selectedFile || preview) ? 'border-solid' : 'border-dashed'
      ]"
      @dragenter.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <input
        type="file"
        ref="fileInput"
        class="hidden"
        @change="handleFileSelect"
        accept="image/*,video/mp4"
        :disabled="!!preview"
      />
      
      <div v-if="!selectedFile && !preview" class="flex flex-col items-center p-6 text-center">
        <div class="p-4 rounded-full bg-white/10 mb-4">
          <LinkIcon class="w-8 h-8 text-primary" />
        </div>
        <p class="text-white">
          Selecione uma imagem ou vídeo
          <span class="block text-sm text-white/60">
            ou arraste e solte aqui
          </span>
        </p>
      </div>

      <div v-else class="relative w-full h-full flex items-center justify-center p-4">
        <!-- Exibir imagem ou vídeo com base no tipo de arquivo -->
        <template v-if="isImage">
          <img
            :src="previewUrl || preview"
            alt="Preview"
            class="max-w-full max-h-[200px] rounded object-contain transition-all duration-300"
            :class="{ 'blur-md grayscale brightness-50': hasSpoiler }"
          />
        </template>
        <template v-else>
          <video
            :src="previewUrl || preview"
            controls
            class="max-w-full max-h-[200px] rounded object-contain transition-all duration-300"
            :class="{ 'blur-md grayscale brightness-50': hasSpoiler }"
          />
        </template>

        <!-- Spoiler toggle -->
        <button
          v-if="(selectedFile || preview) && !isAudio && showSpoiler"
          @click.stop="$emit('update:hasSpoiler', !hasSpoiler)"
          class="absolute top-3 right-12 p-1.5 rounded-full transition-all duration-300 border backdrop-blur-md"
          :class="[
            hasSpoiler 
              ? 'bg-amber-500/20 border-amber-500 text-amber-500' 
              : 'bg-white/10 border-white/20 text-white/60 hover:text-white hover:bg-white/20'
          ]"
          :title="hasSpoiler ? 'Remover spoiler' : 'Adicionar spoiler'"
        >
          <EyeOff v-if="hasSpoiler" class="w-4 h-4" />
          <Eye v-else class="w-4 h-4" />
        </button>

        <!-- Botão de remover só aparece se não estiver no maodo de prévia -->
        <button
          v-if="selectedFile || preview"
          @click.stop="removeFile"
          class="absolute top-3 right-3 p-1.5 rounded-full transition-colors border border-red-500 bg-red-500/10 hover:bg-red-500/20 group"
          aria-label="Remover mídia"
        >
          <XIcon class="w-4 h-4 text-red-500 group-hover:scale-110 transition-transform" />
        </button>
      </div>

      <div
        v-if="uploading && !preview"
        class="absolute inset-x-0 bottom-0 bg-black/70 p-2"
      >
        <div 
          class="h-1 bg-emerald-400 transition-all duration-300"
          :style="{ width: `${progress}%` }"
        />
        <span class="block text-center text-sm text-white mt-1">
          {{ progress }}%
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { LinkIcon, XIcon, Eye, EyeOff } from 'lucide-vue-next'
import { Label } from '../label';
import { useToast } from '../toast';

const { toast } = useToast()

interface Props {
  label?: string
  modelValue?: File | null // Para v-model
  preview?: string // URL da prévia existente (opcional)
  hasSpoiler?: boolean
  showSpoiler?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Mídia inicial',
  modelValue: null,
  preview: '',
  showSpoiler: true
})

const emit = defineEmits(['update:modelValue', 'update:preview', 'update:hasSpoiler']) // Emitindo o evento para atualizar o v-model

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const selectedFile = ref<File | null>(props.modelValue || null) // Inicializando com o valor do v-model
const previewUrl = ref('')
const error = ref('')
const uploading = ref(false)
const progress = ref(0)
const isImage = ref(true)
const isAudio = computed(() => {
  if (selectedFile.value) return selectedFile.value.type.startsWith('audio/')
  if (props.preview) return props.preview.match(/\.(mp3|wav|ogg|aac|m4a)$/) !== null
  return false
})

// Verificar se a prévia é uma imagem ou vídeo
if (props.preview) {
  isImage.value = props.preview.startsWith('data:image') || props.preview.match(/\.(jpeg|jpg|gif|png|avif)$/) !== null
}

// Atualizar o previewUrl quando o modelValue mudar externamente
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    selectedFile.value = newValue
    previewUrl.value = URL.createObjectURL(newValue)
  } else {
    selectedFile.value = null
    previewUrl.value = ''
  }
})

const triggerFileInput = () => {
  if (!selectedFile.value && !props.preview) {
    fileInput.value?.click()
  }
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  processFile(file)
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  const file = event.dataTransfer?.files[0]
  processFile(file)
}

const processFile = (file?: File) => {
  if (!file || props.preview) return // Ignora se estiver no modo de prévia
  
  if (file.type.startsWith('image/')) {
    isImage.value = true
  } else if (file.type.startsWith('video/')) {
    isImage.value = false
  } else {
    toast({
      variant: 'destructive',
      description: 'Por favor, selecione apenas arquivos de imagem ou vídeo.'
    })
    error.value = 'Por favor, selecione apenas arquivos de imagem ou vídeo.'
    return
  }

  if (file.type.startsWith('image/') && file.size > 5 * 1024 * 1024 || file.type.startsWith('video/') && file.size > 50 * 1024 * 1024) {
    toast({
      variant: 'destructive',
      description: 'O arquivo deve ter menos que 5MB.'
    })
    error.value = 'O arquivo deve ter menos que 5MB.'
    return
  }

  error.value = ''
  selectedFile.value = file
  previewUrl.value = URL.createObjectURL(file)
  emit('update:modelValue', file) // Emitindo o novo valor para o v-model
  simulateUpload()
}

const removeFile = () => {
  selectedFile.value = null
  previewUrl.value = ''
  error.value = ''
  progress.value = 0
  uploading.value = false
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  emit('update:preview', null) // Emitindo null para o v-model ao remover o arquivo
  emit('update:modelValue', null) // Emitindo null para o v-model ao remover o arquivo
}

const simulateUpload = () => {
  if (props.preview) return // Ignora se estiver no modo de prévia

  uploading.value = true
  progress.value = 0
  
  const interval = setInterval(() => {
    progress.value += 10
    if (progress.value >= 100) {
      clearInterval(interval)
      uploading.value = false
    }
  }, 70)
}
</script>